import { useState, useEffect } from "react";
import { Form } from "antd";
import { deleteApi, getApi, postApi, putApi } from "../../services/api";
import { toast } from "react-toastify";

const useCourses = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [filterCategory, setFilterCategory] = useState("");
	const [filterPrerequisites, setFilterPrerequisites] = useState("");
	const [filterDuration, setFilterDuration] = useState("");
	const [courses, setCourses] = useState("");
	const [filteredCourses, setFilteredCourses] = useState(courses);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [modalType, setModalType] = useState("add");
	const [formType, setFormType] = useState("course");
	const [selectedCourse, setSelectedCourse] = useState();
	const [form] = Form.useForm();
	const [categories, setCategories] = useState([]);
	const [preRequisites, setPreRequisites] = useState([]);
	const [teachers, setTeachers] = useState([]);

	// Function to fetch teachers
	const fetchTeachers = async () => {
		try {
			const teacherResponse = await getApi(`/users`, { role: "teacher" }); // Fetch teachers from the endpoint
			setTeachers(
				teacherResponse.map((item) => ({
					id: item._id,
					fullName: item.fullName,
				}))
			);
		} catch (error) {
			toast.error("Failed to fetch teachers:", error);
		}
	};

	// Function to fetch categories
	const fetchCategories = async () => {
		try {
			const categoryResponse = await getApi("/courseCategory"); // Fetch categories from the endpoint
			setCategories(
				categoryResponse.map((item) => ({
					id: item._id,
					title: item.title,
				}))
			);
		} catch (error) {
			toast.error("Failed to fetch categories:", error);
		}
	};

	const fetchCourses = async () => {
		try {
			const courses = await getApi("/courses");
			setCourses(courses);
		} catch (error) {
			toast.error("Failed to fetch courses:", error);
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				// Fetch courses
				const courses = await getApi("/courses"); // Adjust the endpoint URL as needed

				setPreRequisites(courses);

				fetchTeachers();
				fetchCategories();
				fetchCourses();
			} catch (error) {
				toast.error("Failed to fetch courses:", error);
			}
		};

		fetchData();
	}, []);

	useEffect(() => {
		const filterCourses = () => {
			return Array.isArray(courses)
				? courses?.filter(
						(course) =>
							course.name
								.toLowerCase()
								.includes(searchTerm.toLowerCase()) &&
							(filterCategory
								? course.category._id === filterCategory
								: true) &&
							(filterPrerequisites
								? course.prerequisiteCourses.length > 0 &&
								  course.prerequisiteCourses.filter(
										(pre) => pre._id === filterPrerequisites
								  )
								: true)
				  )
				: courses;
		};
		setFilteredCourses(filterCourses());
	}, [searchTerm, courses, filterCategory, filterPrerequisites]);

	useEffect(() => {
		setFilteredCourses(courses);
	}, [courses]);

	const showModal = (type, formType, course = null) => {
		setSelectedCourse(course);
		setFormType(formType);
		setModalType(type);
		if (type === "edit") {
			// Ensure that availableTimes are set for viewing
			form.setFieldsValue({
				...course,
				category: course.category._id,
				sessionTimes: course?.sessionTimes.map((time) => ({
					startTime: time.startTime,
					endTime: time.endTime,
					teacherId: time.teacherId,
				})),
			});
		} else {
			form.resetFields();
		}
		setIsModalVisible(true);
	};

	const handleModalConfirm = async (values) => {
		const totalDurationMinutes =
			values.durationHours * 60 + values.durationMinutes;
		if (modalType === "delete") {
			try {
				await deleteApi(`/courses/${selectedCourse._id}`);
				toast.success("Course deleted successfully");
				setIsModalVisible(false);
			} catch (error) {
				toast.error(`Failed to ${modalType} course:`, error);
			} finally {
				return;
			}
		}
		const isEditMode = modalType === "edit";
		// Process the teachers array
		const formattedTeachers = isEditMode
			? []
			: values.teachers.map((teacher) => ({
					teacherId: teacher.teacher,
					startTime: teacher.startTime, // Format startTime as string
					scheduledates: teacher.scheduledates,
			  }));
		// Construct courseData
		const courseData = {
			name: values.name,
			description: values.description,
			category: values.category,
			teachers: isEditMode
				? selectedCourse.teachers
				: [
						...new Set(
							formattedTeachers.map(
								(teacher) => teacher.teacherId._id
							)
						),
				  ], // Send the processed teachers array
			durationInMinutes: totalDurationMinutes,
			maxStudents: values.maxStudents,
			sessionTimes: isEditMode
				? selectedCourse.sessionTimes
				: formattedTeachers.map((teacher) =>
						teacher.startTime.format("HH:mm A")
				  ), // Collect session times
			price: values.price,
			schedule: isEditMode
				? selectedCourse.schedule
				: formattedTeachers.map((teacher) => teacher.scheduledates),

			prerequisiteCourses: Array.isArray(values.prerequisites)
				? values.prerequisites
				: [values.prerequisites], // Ensure prerequisites are included
		};

		const classroomDatas = isEditMode
			? []
			: formattedTeachers.map((teacher) => ({
					userId: teacher.teacherId,
					sessionTime: teacher.startTime.format("HH:mm:ss A"),
			  }));

		const scheduleDatas = isEditMode
			? []
			: formattedTeachers.map((teacher) =>
					teacher.scheduledates.map((date) => ({
						userId: teacher.teacherId,
						scheduleDate: date,
						startTime: teacher.startTime.format("HH:mm:ss A"),
						endTime: new Date(
							teacher.startTime + totalDurationMinutes * 60 * 1000
						).toLocaleTimeString(),
					}))
			  );
		const formData = new FormData();
		formData.append("courseData", JSON.stringify(courseData));
		if (values.image?.originFileObj) {
			formData.append("file", values.image?.originFileObj);
		}
		if (!isEditMode) {
			formData.append("classroomDatas", JSON.stringify(classroomDatas));
			formData.append("scheduleDatas", JSON.stringify(scheduleDatas));
		}
		try {
			isEditMode
				? await putApi(`/courses/${selectedCourse._id}`, formData)
				: await postApi("/courses", formData);

			setIsModalVisible(false);
		} catch (error) {
			toast.error(`Failed to ${modalType} course:`, error);
		}
	};

	const handleModalCancel = () => {
		setIsModalVisible(false);
	};

	return {
		searchTerm,
		setSearchTerm,
		filterCategory,
		setFilterCategory,
		filterPrerequisites,
		setFilterPrerequisites,
		filterDuration,
		setFilterDuration,
		isModalVisible,
		setIsModalVisible,
		modalType,
		setModalType,
		formType,
		setFormType,
		selectedCourse,
		setSelectedCourse,
		form,
		categories,
		preRequisites,
		teachers,
		filteredCourses,
		showModal,
		handleModalConfirm,
		handleModalCancel,
		fetchTeachers,
		fetchCategories, // Expose the functions for external calls
	};
};

export default useCourses;
